import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Chip,
  InputAdornment,
  CircularProgress,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { projectsApi, purchaseOrdersApi } from '../services/api';

function GlobalSearch() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ projects: [], purchaseOrders: [] });
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setLoading(true);
        try {
          const [projectsResponse, posResponse] = await Promise.all([
            projectsApi.getAll(null, true),
            purchaseOrdersApi.getAll(true)
          ]);

          const searchLower = searchQuery.toLowerCase();

          // Filter projects
          const filteredProjects = projectsResponse.data.filter((project) => 
            (project.project_id && project.project_id.toString().toLowerCase().includes(searchLower)) ||
            (project.project_no && project.project_no.toString().toLowerCase().includes(searchLower)) ||
            (project.organisation_name && project.organisation_name.toLowerCase().includes(searchLower)) ||
            (project.quote_reference && project.quote_reference.toLowerCase().includes(searchLower)) ||
            (project.address_line1 && project.address_line1.toLowerCase().includes(searchLower)) ||
            (project.city && project.city.toLowerCase().includes(searchLower)) ||
            (project.postcode && project.postcode.toLowerCase().includes(searchLower))
          ).slice(0, 5); // Limit to 5 results

          // Filter purchase orders
          const filteredPOs = posResponse.data.filter(po => 
            po.vendor_name?.toLowerCase().includes(searchLower) ||
            (po.purchase_order_no && po.purchase_order_no.toString().includes(searchLower)) ||
            (po.purchase_order_id && po.purchase_order_id.toString().includes(searchLower)) ||
            (po.project_no && po.project_no.toString().includes(searchLower)) ||
            (po.project_id && po.project_id.toString().includes(searchLower)) ||
            (po.reference && po.reference.toLowerCase().includes(searchLower)) ||
            (po.created_by_name && po.created_by_name.toLowerCase().includes(searchLower))
          ).slice(0, 5); // Limit to 5 results

          setResults({
            projects: filteredProjects,
            purchaseOrders: filteredPOs
          });
          setShowResults(true);
        } catch (error) {
          console.error('Search error:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setResults({ projects: [], purchaseOrders: [] });
        setShowResults(false);
      }
    }, 300); // 300ms delay

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const handleProjectClick = (projectId) => {
    navigate(`/projects/${projectId}`);
    setSearchQuery('');
    setShowResults(false);
  };

  const handlePOClick = (poId) => {
    navigate(`/purchase-orders/${poId}`);
    setSearchQuery('');
    setShowResults(false);
  };

  const getStatusChip = (status) => {
    let color;
    switch (status) {
      case 'Quote':
        color = 'warning';
        break;
      case 'Sales Order':
        color = 'success';
        break;
      case 'Completed':
        color = 'primary';
        break;
      case 'Cancelled':
        color = 'error';
        break;
      default:
        color = 'default';
    }
    return <Chip label={status} color={color} size="small" />;
  };

  const hasResults = results.projects.length > 0 || results.purchaseOrders.length > 0;

  return (
    <Box ref={searchRef} sx={{ position: 'relative', width: { xs: 200, sm: 300, md: 400 } }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search projects & POs..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onFocus={() => searchQuery.trim().length >= 2 && setShowResults(true)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: loading && (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ),
          sx: { 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.14)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
            },
            '& input': {
              color: 'text.primary',
            },
            '& input::placeholder': {
              color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)',
              opacity: 1,
            },
            '& .MuiInputAdornment-root': {
              color: 'text.secondary',
            }
          }
        }}
      />

      {showResults && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 1,
            maxHeight: 400,
            overflow: 'auto',
            zIndex: 1300,
          }}
        >
          {!hasResults && searchQuery.trim().length >= 2 && !loading && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">No results found</Typography>
            </Box>
          )}

          {results.projects.length > 0 && (
            <>
              <Box sx={{ p: 1.5, pb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  PROJECTS
                </Typography>
              </Box>
              <List dense disablePadding>
                {results.projects.map((project) => (
                  <ListItem key={project.project_id} disablePadding>
                    <ListItemButton onClick={() => handleProjectClick(project.project_id)}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {project.organisation_name}
                            </Typography>
                            {getStatusChip(project.status)}
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {project.quote_reference || `Project #${project.project_no || project.project_id}`} • 
                            {' '}{project.address_line1}, {project.city}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {results.projects.length > 0 && results.purchaseOrders.length > 0 && (
            <Divider />
          )}

          {results.purchaseOrders.length > 0 && (
            <>
              <Box sx={{ p: 1.5, pb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  PURCHASE ORDERS
                </Typography>
              </Box>
              <List dense disablePadding>
                {results.purchaseOrders.map((po) => (
                  <ListItem key={po.purchase_order_id} disablePadding>
                    <ListItemButton onClick={() => handlePOClick(po.purchase_order_id)}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              PO-{po.purchase_order_no || po.purchase_order_id}
                            </Typography>
                            <Chip 
                              label={po.vendor_name || 'No vendor'} 
                              size="small" 
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {po.reference || 'No reference'}
                            {po.project_id && ` • Project #${po.project_no || po.project_id}`}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default GlobalSearch;
